// src/pages/Checkout.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

// API même domaine (Vercel Functions)
const API_BASE = ""; // => fetch("/api/checkout/create", ...)

// Helper: POST JSON robuste
async function postJSON(url, body) {
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await resp.text();
  let data;
  try { data = JSON.parse(text); }
  catch {
    console.error("Réponse non-JSON (souvent HTML):", text.slice(0, 400));
    throw new Error(`Réponse non-JSON (${resp.status}) depuis ${url}`);
  }
  if (!resp.ok) {
    const msg = data?.provider_message || data?.message || data?.error || `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return data;
}

export default function Checkout() {
  const navigate = useNavigate();
  const { items, updateQty, removeItem, clear, total } = useCart();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "" });
  const [quote, setQuote] = useState(null); // {net, fee, gross, currency}
  const currency = "XOF";

  // total (cents) -> XOF entiers, min 100
  const amountNetXOF = useMemo(
    () => Math.max(100, Math.round((total || 0) / 100)),
    [total]
  );

  const canPay =
    items.length > 0 && customer.name.trim() && customer.phone.trim();

  const onChange = (e) =>
    setCustomer((c) => ({ ...c, [e.target.name]: e.target.value }));

  const onPay = async (e) => {
    e.preventDefault();
    setErr("");
    if (!canPay) return;

    try {
      setLoading(true);
      const orderId = `CMD-${Date.now()}`;

      const payload = {
        orderId,
        amount: amountNetXOF, // NET XOF entiers
        currency,
        channels: "MOBILE_MONEY", // ou "ALL" si tu veux tout ouvrir
        customer: {
          name: customer.name,
          email: customer.email || undefined,
          phone: customer.phone,
          address: "",
          city: "",
          country: "TG",
          state: "TG",
          zip: "00000",
        },
        items: items.map((i) => ({
          productId: i.productId,
          variantKey: i.variantKey,
          qty: i.qty,
        })),
      };

      const data = await postJSON(`${API_BASE}/api/checkout/create`, payload);

      // Afficher Net/Frais/Total renvoyés par l’API (optionnel)
      setQuote(data.amounts || null);

      // Redirection vers CinetPay
      if (!data?.payment_url) throw new Error("payment_url manquant");
      window.location.href = data.payment_url;
    } catch (e2) {
      console.error(e2);
      setErr(e2.message || "Impossible d’initier le paiement.");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container py-4">
        <div className="alert alert-info">
          Votre panier est vide.{" "}
          <button className="btn btn-link p-0" onClick={() => navigate("/catalogue")}>
            Parcourir le catalogue →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <h1 className="h5 mb-3">Paiement</h1>
      <div className="row g-4">
        {/* Formulaire client */}
        <div className="col-lg-7">
          <form className="card" onSubmit={onPay}>
            <div className="card-body">
              <h2 className="h6 mb-3">Vos informations</h2>

              <div className="mb-2">
                <label className="form-label">Nom complet</label>
                <input className="form-control" name="name" value={customer.name} onChange={onChange} required />
              </div>
              <div className="mb-2">
                <label className="form-label">Téléphone</label>
                <input className="form-control" name="phone" value={customer.phone} onChange={onChange} required />
              </div>
              <div className="mb-2">
                <label className="form-label">Email (optionnel)</label>
                <input className="form-control" type="email" name="email" value={customer.email} onChange={onChange} />
              </div>

              {err && <div className="alert alert-danger mt-2">{err}</div>}

              {quote && (
                <div className="alert alert-secondary mt-3 mb-0">
                  <div className="d-flex justify-content-between">
                    <span>Montant (net)</span><strong>{quote.net} {quote.currency}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Frais de transaction</span><strong>{quote.fee} {quote.currency}</strong>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between">
                    <span>Total débité</span><strong>{quote.gross} {quote.currency}</strong>
                  </div>
                </div>
              )}
            </div>
            <div className="card-footer text-end">
              <button className="btn btn-dark" type="submit" disabled={!canPay || loading}>
                {loading ? "Redirection vers CinetPay…" : "Payer maintenant"}
              </button>
            </div>
          </form>
        </div>

        {/* Récapitulatif */}
        <div className="col-lg-5">
          <div className="card">
            <div className="card-body">
              <h2 className="h6 mb-3">Résumé</h2>
              <ul className="list-group list-group-flush mb-3">
                {items.map((i) => (
                  <li key={`${i.productId}-${i.variantKey}`} className="list-group-item d-flex align-items-center">
                    <div className="me-auto">
                      <div className="fw-semibold text-truncate" title={i.title}>{i.title}</div>
                      <div className="text-muted small">{i.variantKey !== '|' ? i.variantKey : '—'}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => updateQty(i.productId, i.variantKey, i.qty - 1)}>-</button>
                      <span className="small">{i.qty}</span>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => updateQty(i.productId, i.variantKey, i.qty + 1)}>+</button>
                    </div>
                    <div className="ms-3" style={{ width: 100, textAlign: "right" }}>
                      {((i.price * i.qty)).toFixed(0)} {currency}
                    </div>
                    <button className="btn btn-sm btn-outline-danger ms-2" onClick={() => removeItem(i.productId, i.variantKey)}>×</button>
                  </li>
                ))}
              </ul>

              <div className="d-flex justify-content-between">
                <span>Total (net)</span>
                <strong>{amountNetXOF.toFixed(0)} {currency}</strong>
              </div>
              <div className="text-muted small mt-1">
                Les frais éventuels seront ajoutés par l’API avant la redirection.
              </div>

              <button className="btn btn-outline-secondary mt-3 w-100" onClick={clear}>
                Vider le panier
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
